import { useState, useEffect, useId } from "react";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Cookie } from "lucide-react";

type Copy = {
  bannerTitle: string;
  bannerBody: string;
  readPolicy: string;
  acceptAll: string;
  rejectAll: string;
  customize: string;
  save: string;
  prefsTitle: string;
  prefsDesc: string;
  necessaryTitle: string;
  necessaryDesc: string;
  analyticsTitle: string;
  analyticsDesc: string;
  marketingTitle: string;
  marketingDesc: string;
  alwaysOn: string;
};

const COPY: Record<string, Copy> = {
  en: {
    bannerTitle: "We use cookies",
    bannerBody:
      "We use essential cookies to run this site and, with your consent, analytics and marketing cookies to measure and improve it. You can accept, reject, or choose per category. Essential cookies cannot be rejected.",
    readPolicy: "Read our Cookie Policy",
    acceptAll: "Accept all",
    rejectAll: "Reject all",
    customize: "Customize",
    save: "Save preferences",
    prefsTitle: "Cookie preferences",
    prefsDesc: "Choose which categories of cookies you allow. You can change this at any time from the footer.",
    necessaryTitle: "Strictly necessary",
    necessaryDesc: "Required for the site to work — page navigation, security, and remembering your cookie choices.",
    analyticsTitle: "Analytics",
    analyticsDesc: "Google Analytics — helps us understand how the site is used so we can improve it.",
    marketingTitle: "Marketing",
    marketingDesc: "Leadsy — visitor identification and campaign attribution for our marketing measurement.",
    alwaysOn: "Always on",
  },
  pl: {
    bannerTitle: "Używamy plików cookie",
    bannerBody:
      "Używamy niezbędnych plików cookie do działania serwisu, a za Twoją zgodą — plików analitycznych i marketingowych, aby mierzyć i ulepszać serwis. Możesz zaakceptować, odrzucić lub wybrać kategorie. Niezbędnych plików cookie nie można odrzucić.",
    readPolicy: "Przeczytaj Politykę Cookies",
    acceptAll: "Akceptuj wszystkie",
    rejectAll: "Odrzuć wszystkie",
    customize: "Dostosuj",
    save: "Zapisz preferencje",
    prefsTitle: "Preferencje plików cookie",
    prefsDesc: "Wybierz kategorie plików cookie, na które wyrażasz zgodę. Możesz to zmienić w dowolnym momencie w stopce.",
    necessaryTitle: "Ściśle niezbędne",
    necessaryDesc: "Wymagane do działania strony — nawigacja, bezpieczeństwo i zapamiętanie Twoich wyborów dotyczących cookies.",
    analyticsTitle: "Analityczne",
    analyticsDesc: "Google Analytics — pomaga zrozumieć, jak korzystasz z serwisu, abyśmy mogli go ulepszać.",
    marketingTitle: "Marketingowe",
    marketingDesc: "Leadsy — identyfikacja odwiedzających i pomiar skuteczności naszych działań marketingowych.",
    alwaysOn: "Zawsze aktywne",
  },
  by: {
    bannerTitle: "Мы выкарыстоўваем cookies",
    bannerBody:
      "Мы выкарыстоўваем неабходныя cookies для працы сайта і, з вашай згоды, аналітычныя і маркетынгавыя cookies, каб паляпшаць сайт. Вы можаце прыняць, адхіліць або выбраць катэгорыі. Неабходныя cookies нельга адхіліць.",
    readPolicy: "Прачытаць Палітыку Cookies",
    acceptAll: "Прыняць усе",
    rejectAll: "Адхіліць усе",
    customize: "Наладзіць",
    save: "Захаваць налады",
    prefsTitle: "Налады cookies",
    prefsDesc: "Выберыце катэгорыі cookies, якія вы дазваляеце. Вы можаце змяніць гэта ў любы момант у падвале сайта.",
    necessaryTitle: "Строга неабходныя",
    necessaryDesc: "Патрэбныя для працы сайта — навігацыя, бяспека і захаванне вашых наладаў cookies.",
    analyticsTitle: "Аналітычныя",
    analyticsDesc: "Google Analytics — дапамагае зразумець, як выкарыстоўваецца сайт, каб мы маглі яго паляпшаць.",
    marketingTitle: "Маркетынгавыя",
    marketingDesc: "Leadsy — ідэнтыфікацыя наведвальнікаў і вымярэнне эфектыўнасці нашых маркетынгавых кампаній.",
    alwaysOn: "Заўсёды ўключана",
  },
};

export function CookieBanner() {
  const noticeId = useId();
  const { language } = useLanguage();
  const t = (COPY[language] ?? COPY["en"])!;
  const { hasDecided, consent, acceptAll, rejectAll, save, isPreferencesOpen, openPreferences, closePreferences } = useCookieConsent();

  const [analytics, setAnalytics] = useState(consent.analytics);
  const [marketing, setMarketing] = useState(consent.marketing);

  useEffect(() => {
    setAnalytics(consent.analytics);
    setMarketing(consent.marketing);
  }, [consent, isPreferencesOpen]);

  const prefix = language === "pl" ? "/pl" : language === "by" ? "/by" : "";
  const cookiePolicyHref = `${prefix}/cookie-policy`;

  // Wait for webfonts before painting the banner. Rendering it with the
  // fallback font and re-flowing once Inter swaps in is the main source of
  // layout shift (CLS) on the page.
  const [fontsReady, setFontsReady] = useState(
    typeof document === "undefined" ? true : (document as Document).fonts?.status === "loaded",
  );
  useEffect(() => {
    if (fontsReady) return;
    let cancelled = false;
    const done = () => !cancelled && setFontsReady(true);
    document.fonts?.ready.then(done);
    const fallback = setTimeout(done, 2000);
    return () => {
      cancelled = true;
      clearTimeout(fallback);
    };
  }, [fontsReady]);

  // Once React is hydrated, React handlers take over from the pre-hydration
  // native fallback script (see COOKIE_PREHYDRATION_SCRIPT in __root.tsx).
  useEffect(() => {
    (window as unknown as { __cookieConsentHydrated?: boolean }).__cookieConsentHydrated = true;
  }, []);

  const showBanner = !hasDecided && fontsReady;


  return (
    <>
      {showBanner && (
        <aside
          aria-labelledby={`${noticeId}-title`}
          aria-describedby={`${noticeId}-description`}
          data-cookie-banner
          className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6 pointer-events-none"
        >
          <div className="pointer-events-auto mx-auto max-w-4xl rounded-lg border border-border bg-card shadow-2xl p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-3">
              <Cookie className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p id={`${noticeId}-title`} className="text-base font-semibold text-foreground">{t.bannerTitle}</p>
                <p id={`${noticeId}-description`} className="text-sm text-foreground/75 mt-1">
                  {t.bannerBody}{" "}
                  <Link to={cookiePolicyHref} className="text-primary underline underline-offset-2">
                    {t.readPolicy}
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:justify-end">
              <div className="flex items-center gap-4 sm:mr-2">
                <button
                  type="button"
                  data-cookie-action="reject"
                  onClick={rejectAll}
                  className="text-xs text-foreground/80 hover:text-foreground underline underline-offset-2 transition-colors"
                >
                  {t.rejectAll}
                </button>
                <button
                  type="button"
                  onClick={openPreferences}
                  className="text-xs text-foreground/80 hover:text-foreground underline underline-offset-2 transition-colors"
                >
                  {t.customize}
                </button>
              </div>
              <Button
                size="lg"
                data-cookie-action="accept"
                className="bg-primary text-primary-foreground font-semibold hover:opacity-90 shadow-lg shadow-primary/20 sm:min-w-[200px]"
                onClick={acceptAll}
              >
                {t.acceptAll}
              </Button>
            </div>
          </div>
        </aside>
      )}

      <Dialog open={isPreferencesOpen} onOpenChange={(o) => (o ? openPreferences() : closePreferences())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.prefsTitle}</DialogTitle>
            <DialogDescription>{t.prefsDesc}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <CategoryRow title={t.necessaryTitle} desc={t.necessaryDesc} checked disabled alwaysOnLabel={t.alwaysOn} />
            <CategoryRow
              title={t.analyticsTitle}
              desc={t.analyticsDesc}
              checked={analytics}
              onChange={setAnalytics}
            />
            <CategoryRow
              title={t.marketingTitle}
              desc={t.marketingDesc}
              checked={marketing}
              onChange={setMarketing}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-2">
            <Button variant="outline" size="sm" onClick={rejectAll}>
              {t.rejectAll}
            </Button>
            <Button variant="outline" size="sm" onClick={acceptAll}>
              {t.acceptAll}
            </Button>
            <Button
              size="sm"
              className="bg-primary text-primary-foreground hover:opacity-90"
              onClick={() => save({ analytics, marketing })}
            >
              {t.save}
            </Button>
          </div>

          <p className="text-xs text-foreground/80 pt-2">
            <Link to={cookiePolicyHref} className="text-primary underline underline-offset-2" onClick={closePreferences}>
              {t.readPolicy}
            </Link>
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CategoryRow({
  title,
  desc,
  checked,
  disabled,
  alwaysOnLabel,
  onChange,
}: {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  alwaysOnLabel?: string;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
      <div className="min-w-0">
        <p className="font-medium text-foreground">{title}</p>
        <p className="text-sm text-foreground/70 mt-1">{desc}</p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {disabled ? (
          <>
            <Switch checked disabled />
            {alwaysOnLabel && <span className="text-xs text-foreground/80">{alwaysOnLabel}</span>}
          </>
        ) : (
          <Switch checked={checked} onCheckedChange={onChange ?? (() => {})} />
        )}
      </div>
    </div>
  );
}
